import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputBase,
  InputLabel,
  Typography,
} from '@mui/material';
import { ndcRegex } from 'common/constants/regex';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

const inputBaseSx = {
  'label + &': { marginTop: '1.3em' },
  marginTop: '1.3em',
  boxSizing: 'border-box',
  borderRadius: 1,
  marginBottom: 0.25,
  padding: '4px 8px',
  '&:focus, &:focus&:invalid, &.Mui-focused': { borderWidth: 2, padding: '3px 7px' },
};

const StyledField = ({ id, label, value, onChange, onBlur, error, helperText }) => (
  <FormControl error={error} variant='standard' fullWidth>
    <InputLabel shrink htmlFor={id} sx={{ fontSize: '1.2em' }}>
      {label}
    </InputLabel>
    <InputBase id={id} sx={inputBaseSx} value={value} onChange={onChange} onBlur={onBlur} />
    <FormHelperText sx={{ minHeight: '0.5rem' }}>{helperText}</FormHelperText>
  </FormControl>
);

StyledField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  helperText: PropTypes.string,
};

StyledField.defaultProps = { helperText: '' };

const validateEntry = (entry, allEntries, index) => {
  const errors = {};
  if (!entry.ndcCode?.trim()) {
    errors.ndcCode = 'Required';
  } else if (!ndcRegex.test(entry.ndcCode.trim())) {
    errors.ndcCode = 'Invalid NDC format (e.g. 12345-678-90)';
  } else {
    const isDuplicate = allEntries.some(
      (e, i) => i !== index && e.ndcCode?.trim() === entry.ndcCode.trim()
    );
    if (isDuplicate) errors.ndcCode = 'Duplicate NDC';
  }
  if (!entry.quantity && entry.quantity !== 0) {
    errors.quantity = 'Required';
  } else {
    const num = Number(entry.quantity);
    if (Number.isNaN(num)) errors.quantity = 'Must be a number';
    else if (num <= 0) errors.quantity = 'Must be greater than 0';
  }
  return errors;
};

const UpdateNdcForm = ({ rowData, onClose, onSave }) => {
  const initialEntries = useRef(() => {
    const overRides = rowData?.overRides;
    if (
      rowData?.ndcUpdateSubmitted &&
      overRides?.length === 1 &&
      overRides[0].ndcCode === rowData.originalNdc &&
      String(overRides[0].quantity) === String(rowData.quantity)
    ) {
      return [{ ndcCode: '', quantity: '' }];
    }
    return overRides?.length
      ? overRides.map((item) => ({
          ndcCode: item.ndcCode ?? '',
          quantity: String(item.quantity ?? ''),
        }))
      : [{ ndcCode: '', quantity: '' }];
  }).current();

  const [entries, setEntries] = useState(() => initialEntries);

  const [touched, setTouched] = useState({});

  const allErrors = entries.map((entry, i) => validateEntry(entry, entries, i));
  const isFormValid = allErrors.every((e) => Object.keys(e).length === 0);
  const isDirty = JSON.stringify(entries) !== JSON.stringify(initialEntries);

  const touch = (index, field) => setTouched((prev) => ({ ...prev, [`${index}.${field}`]: true }));

  const updateEntry = (index, field, value) =>
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));

  const addEntry = () => {
    setEntries((prev) => [...prev, { ndcCode: '', quantity: '' }]);
  };

  const removeEntry = (index) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setTouched((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const [ki] = key.split('.');
        const ki2 = Number(ki);
        if (ki2 < index) next[key] = prev[key];
        else if (ki2 > index) next[`${ki2 - 1}.${key.split('.')[1]}`] = prev[key];
      });
      return next;
    });
  };

  return (
    <>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' color='text.secondary' gutterBottom>
            Original
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              p: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {rowData?.originalNdc}
            </Typography>
            <Typography color='text.secondary' variant='body2'>
              Qty: <strong>{rowData?.quantity}</strong>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
          Updated NDC(s)
        </Typography>

        {entries.map((entry, index) => {
          const errs = allErrors[index];
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}
            >
              <Box sx={{ flex: 2 }}>
                <StyledField
                  id={`ndcCode-${index}`}
                  label='NDC *'
                  value={entry.ndcCode}
                  onChange={(e) => updateEntry(index, 'ndcCode', e.target.value)}
                  onBlur={() => touch(index, 'ndcCode')}
                  error={!!touched[`${index}.ndcCode`] && !!errs.ndcCode}
                  helperText={touched[`${index}.ndcCode`] ? errs.ndcCode : ''}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StyledField
                  id={`quantity-${index}`}
                  label='Qty *'
                  value={entry.quantity}
                  onChange={(e) => updateEntry(index, 'quantity', e.target.value)}
                  onBlur={() => touch(index, 'quantity')}
                  error={!!touched[`${index}.quantity`] && !!errs.quantity}
                  helperText={touched[`${index}.quantity`] ? errs.quantity : ''}
                />
              </Box>
              {entries.length > 1 && (
                <IconButton
                  size='small'
                  onClick={() => removeEntry(index)}
                  color='error'
                  sx={{ mt: 1.5 }}
                >
                  <DeleteIcon fontSize='large' />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Button startIcon={<AddIcon />} size='small' onClick={addEntry} sx={{ mt: 0.5 }}>
          Add More NDC
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined'>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={!isFormValid || !isDirty}
          onClick={() => {
            onSave(entries.map(({ ndcCode, quantity }) => ({ ndcCode, quantity })));
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};

UpdateNdcForm.propTypes = {
  rowData: PropTypes.shape({
    fillHistoryId: PropTypes.string,
    originalNdc: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ndcUpdateSubmitted: PropTypes.bool,
    overRides: PropTypes.arrayOf(
      PropTypes.shape({
        ndcCode: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

UpdateNdcForm.defaultProps = {
  rowData: null,
};

const UpdateNdcDialog = ({ open, onClose, rowData, onSave }) => (
  <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
    <DialogTitle>Update NDC</DialogTitle>
    {open && <UpdateNdcForm rowData={rowData} onClose={onClose} onSave={onSave} />}
  </Dialog>
);

UpdateNdcDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.shape({
    fillHistoryId: PropTypes.string,
    originalNdc: PropTypes.string,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    overRides: PropTypes.arrayOf(
      PropTypes.shape({
        ndcCode: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }),
  onSave: PropTypes.func.isRequired,
};

UpdateNdcDialog.defaultProps = {
  rowData: null,
};

export default UpdateNdcDialog;
